/**
 * Die Karte „Was gerade läuft“ — oben in den Einstellungen, direkt unter der Suche. (17.10.2026, Einstellungen neu ordnen B5)
 *
 * Martin: „Hatten wir nicht gesagt, dass wir eine Anzeige machen über die laufenden Prozesse? Wo finde ich die?“
 * Ruhige Zeilen, jede mit Zeichen UND Wort (nie nur Farbe): Schmied, Zimmermann, Hüter (18.10.2026: alle drei mit denselben Worten
 * aus · gesperrt · bereit · hört zu), Hintergrundläufe, Nachtjobs,
 * offene Tickets, Server. 17.10.2026: dazu „Vorschläge: n offen“ (Selbstverbesserung Phase 1) — nur, wenn der Server die Zahl kennt. Die Daten kommen von GET /api/system/lage-karte (server/lage-karte-routen.ts) — nur Zahlen,
 * Booleans und feste Worte.
 *
 * ⚠️ Nur Konto 1 (shared/entwickler-im-chat.ts); jedes andere Konto bekommt GAR KEINE Karte (kein Aufruf am Server).
 * ⚠️ Kein Dauerpolling: beim Bauen ein Abruf, danach alle 30 s — nur solange die Karte im Dokument hängt, der Tab sichtbar
 *    UND die Ansicht „Einstellungen“ die aktive ist (`body[data-activeView]`); bei 401/403 endet der Takt für immer; beim Wiedersichtbarwerden sofort ein Abruf. „Weniger Bewegung“: nichts bewegt sich — kein Pulsieren, kein
 *    Übergang; die Zeilen werden still ersetzt.
 * ⚠️ Fällt der Abruf aus oder fehlt eine Zeile in der Antwort: „unbekannt“ — nie ein Fehler, nie ein erfundenes Grün.
 */
import { entwicklerImChatFuerKonto } from "../../../shared/entwickler-im-chat.js";
import type { EntwicklerId } from "../../../shared/entwickler.js";
import { TEAM_IDS, ZUSTAND_WORT, ZUSTAND_ZEICHEN, entwicklerZustand, teamName, zuletztText, type EntwicklerZustand } from "./entwickler-zustand.js";

/** Die Arten einer Zeile. „bereit“, „hoert“ und „gesperrt“ gehören den drei Entwicklern (entwickler-zustand.ts). */
export type LageArt = "ruhig" | "laeuft" | "aus" | "stoerung" | "unbekannt" | EntwicklerZustand;

/** Das Ereignis, mit dem die Seite „Entwickler“ und der Gesamtschalter melden: ein Kontoschalter eines Entwicklers hat sich geändert. */
export const ENTWICKLER_KONTO_EREIGNIS = "voice-v8:entwickler-konto";

/** Die Kontoschalter der drei Entwickler (roh aus den Einstellungen). */
export interface LageKonto {
  /** Hauptschalter „Entwickler“ (Vorgabe an). */
  gesamt?: boolean;
  entwicklerImChat: boolean;
  zimmermann: boolean;
  hueter: boolean;
}

export interface LageZeile {
  id: EntwicklerId | "laeufe" | "nachtjobs" | "tickets" | "vorschlaege" | "server";
  titel: string;
  art: LageArt;
  /** Das Wort neben dem Zeichen. */
  wert: string;
  /** Optional eine zweite, leise Zeile. */
  satz?: string;
}

/** Die gelesene Antwort — jedes Feld streng typisiert, was nicht passt wird `null`. */
export interface LageStand {
  schmied: boolean | null;
  zimmermann: "bereit" | "gesperrt" | null;
  /** 18.10.2026: der Hüter wie der Zimmermann; fehlt bei einem älteren Server (dann `null`). */
  hueter?: "bereit" | "gesperrt" | null;
  /** 18.10.2026: ist ein Hörer verbunden? Nur ein Boolean; `null`/fehlt: unbekannt. */
  zimmermannHoert?: boolean | null;
  hueterHoert?: boolean | null;
  /** 18.10.2026: Sekunden seit dem letzten Hörer-Kontakt; `null`: seit dem Start nie; fehlt: älterer Server. */
  zuletztS?: { schmied?: number | null; zimmermann?: number | null; hueter?: number | null };
  laeufe: number | null;
  nachtjobs: "fertig" | "laeuft" | "gestoert" | "offen" | null;
  tickets: number | null;
  /** Offene Vorschläge der Selbstverbesserung (17.10.2026); `null`: unbekannt — dann fehlt die Zeile. */
  vorschlaege?: number | null;
  server: { version: string | null; seit: string | null } | null;
  neustart: { frei: boolean; haelt: string[] } | null;
}

export const LAGE_ABRUF_MS = 30_000;
export const LAGE_ZEICHEN: Readonly<Record<LageArt, string>> = { ruhig: "✓", laeuft: "●", stoerung: "!", ...ZUSTAND_ZEICHEN };

/** Sekunden seit dem letzten Kontakt je Entwickler streng lesen: Zahl oder `null`; sonst fehlt der Schlüssel. */
function zuletztAus(r: Record<string, unknown>): NonNullable<LageStand["zuletztS"]> {
  const out: NonNullable<LageStand["zuletztS"]> = {};
  for (const id of TEAM_IDS) {
    const w = r[id];
    if (w === null) out[id] = null;
    else if (typeof w === "number" && Number.isFinite(w) && w >= 0) out[id] = Math.floor(w);
  }
  return out;
}

const zahl = (v: unknown, max: number): number | null =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= max ? Math.floor(v) : null;

/** Streng lesen, was der Server schickt. `null`: gar nicht lesbar (dann ist alles „unbekannt“). Rein. */
export function lageStandAus(roh: unknown): LageStand | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (r.ok !== true) return null;
  const s = r.server as { version?: unknown; seit?: unknown } | null | undefined;
  const n = r.neustart as { frei?: unknown; haelt?: unknown } | null | undefined;
  return {
    schmied: typeof r.schmied === "boolean" ? r.schmied : null,
    zimmermann: r.zimmermann === "bereit" || r.zimmermann === "gesperrt" ? r.zimmermann : null,
    hueter: r.hueter === "bereit" || r.hueter === "gesperrt" ? r.hueter : null,
    zimmermannHoert: typeof r.zimmermannHoert === "boolean" ? r.zimmermannHoert : null,
    hueterHoert: typeof r.hueterHoert === "boolean" ? r.hueterHoert : null,
    ...(r.zuletztS && typeof r.zuletztS === "object" ? { zuletztS: zuletztAus(r.zuletztS as Record<string, unknown>) } : {}),
    laeufe: zahl(r.laeufe, 999),
    nachtjobs: r.nachtjobs === "fertig" || r.nachtjobs === "laeuft" || r.nachtjobs === "gestoert" || r.nachtjobs === "offen" ? r.nachtjobs : null,
    tickets: zahl(r.tickets, 99999),
    vorschlaege: zahl(r.vorschlaege, 999),
    server:
      s && typeof s === "object"
        ? {
            version: typeof s.version === "string" && /^[0-9A-Za-z.+-]{1,40}$/.test(s.version) ? s.version : null,
            seit: typeof s.seit === "string" && Number.isFinite(Date.parse(s.seit)) ? s.seit : null,
          }
        : null,
    neustart:
      n && typeof n === "object" && typeof n.frei === "boolean" && Array.isArray(n.haelt)
        ? { frei: n.frei, haelt: n.haelt.filter((x): x is string => typeof x === "string").slice(0, 6).map((x) => x.slice(0, 40)) }
        : null,
  };
}

/** „seit 3 Std.“ — grob und ruhig. Rein. */
export function seitText(seitIso: string, jetzt: number): string {
  const min = Math.max(0, Math.round((jetzt - Date.parse(seitIso)) / 60_000));
  if (min < 1) return "seit gerade eben";
  if (min < 60) return `seit ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 48) return `seit ${std} Std.`;
  return `seit ${Math.round(std / 24)} Tagen`;
}

const UNBEKANNT = { art: "unbekannt" as LageArt, wert: "unbekannt" };

/**
 * Die Zeile eines Entwicklers — für alle drei dieselben Zustände und Worte (entwickler-zustand.ts):
 * aus (Kontoschalter) · gesperrt (Notschalter/Zugang fehlt) · bereit (an, niemand hört zu) · hört zu (Hörer verbunden). Rein.
 */
export function entwicklerLageZeile(id: EntwicklerId, s: LageStand | null, konto: LageKonto): LageZeile {
  const haupt = konto.entwicklerImChat && konto.gesamt !== false;
  let an = haupt;
  let gesperrt: boolean | null;
  let hoert: boolean | null;
  if (id === "schmied") {
    gesperrt = typeof s?.schmied === "boolean" ? false : null;
    hoert = typeof s?.schmied === "boolean" ? s.schmied : null;
  } else {
    an = haupt && (id === "zimmermann" ? konto.zimmermann : konto.hueter);
    const stand = id === "zimmermann" ? s?.zimmermann : s?.hueter;
    gesperrt = stand === "gesperrt" ? true : stand === "bereit" ? false : null;
    hoert = (id === "zimmermann" ? s?.zimmermannHoert : s?.hueterHoert) ?? null;
  }
  const zuletzt = s?.zuletztS?.[id];
  const z = entwicklerZustand({ an, gesperrt, hoert, ...(zuletzt !== undefined ? { zuletztS: zuletzt } : {}) });
  return { id, titel: teamName(id), art: z, wert: ZUSTAND_WORT[z], ...(z === "getrennt" ? { satz: zuletztText(zuletzt) } : {}) };
}

/** Die Zeilen in Worten (acht, mit bekannter Vorschlagszahl neun). `stand: null` = nicht abrufbar. Rein. */
export function lageZeilen(
  stand: LageStand | null,
  jetzt: number = Date.now(),
  konto: LageKonto = { entwicklerImChat: true, zimmermann: true, hueter: true },
): LageZeile[] {
  const s = stand;
  const lf: Pick<LageZeile, "art" | "wert"> =
    s?.laeufe == null ? UNBEKANNT : s.laeufe > 0 ? { art: "laeuft", wert: s.laeufe === 1 ? "1 läuft" : `${s.laeufe} laufen` } : { art: "ruhig", wert: "keiner" };
  const nj: Pick<LageZeile, "art" | "wert"> =
    s?.nachtjobs === "fertig"
      ? { art: "ruhig", wert: "fertig" }
      : s?.nachtjobs === "laeuft"
        ? { art: "laeuft", wert: "läuft" }
        : s?.nachtjobs === "gestoert"
          ? { art: "stoerung", wert: "gestört" }
          : s?.nachtjobs === "offen"
            ? { art: "aus", wert: "noch nichts heute" }
            : UNBEKANNT;
  const tk: Pick<LageZeile, "art" | "wert"> = s?.tickets == null ? UNBEKANNT : { art: "ruhig", wert: String(s.tickets) };
  let sv: Pick<LageZeile, "art" | "wert"> & { satz?: string } = UNBEKANNT;
  if (s?.server && (s.server.version || s.server.seit)) {
    const teile = [s.server.version, s.server.seit ? seitText(s.server.seit, jetzt) : null].filter(Boolean);
    sv = { art: "ruhig", wert: teile.join(" · ") };
  }
  if (s?.neustart) {
    sv = {
      ...sv,
      satz: s.neustart.frei ? "Neustart möglich" : `Neustart gesperrt${s.neustart.haelt.length ? `: ${s.neustart.haelt.join(", ")}` : ""}`,
    };
  }
  return [
    ...TEAM_IDS.map((id) => entwicklerLageZeile(id, s, konto)),
    { id: "laeufe", titel: "Hintergrundläufe", ...lf },
    { id: "nachtjobs", titel: "Nachtjobs", ...nj },
    { id: "tickets", titel: "Offene Tickets", ...tk },
    ...(s?.vorschlaege != null
      ? [
          {
            id: "vorschlaege" as const,
            titel: "Vorschläge",
            art: (s.vorschlaege > 0 ? "laeuft" : "ruhig") as LageArt,
            wert: s.vorschlaege > 0 ? `${s.vorschlaege} offen` : "keine offen",
          },
        ]
      : []),
    { id: "server", titel: "Server", ...sv },
  ];
}

export interface LageKontext {
  uid: number | null | undefined;
  /** Für Tests ersetzbar: den Stand holen (`null`: nicht abrufbar; `"ende"`: 401/403 — der Takt hört auf). */
  hole?: () => Promise<LageStand | null | "ende">;
  /** Für Tests ersetzbar: zeigt der Nutzer gerade die Einstellungen? Nur dann läuft der Takt. */
  sichtbar?: () => boolean;
  /** Die Kontoschalter der drei Entwickler (für „aus“). Fehlt die Angabe, gelten alle als an. */
  konto?: () => LageKonto;
  /** Für Tests ersetzbar: der Takt (Standard 30 s). */
  takt?: number;
}

/** Die Ansicht „Einstellungen“ ist die aktive (app.ts versteckt die Übersicht nur, sie bleibt im Dokument hängen). */
function einstellungenOffen(): boolean {
  return document.body.dataset.activeView === "settings";
}

async function standVomServer(): Promise<LageStand | null | "ende"> {
  try {
    const r = await fetch("/api/system/lage-karte", { credentials: "same-origin", cache: "no-store" });
    if (r.status === 401 || r.status === 403) return "ende";
    return r.ok ? lageStandAus(await r.json()) : null;
  } catch {
    return null;
  }
}

function zeileDom(z: LageZeile): HTMLElement {
  const li = document.createElement("li");
  li.className = "einst-lage-zeile";
  li.dataset.id = z.id;
  const zeichen = document.createElement("span");
  zeichen.className = "einst-lage-zeichen";
  zeichen.dataset.art = z.art;
  zeichen.setAttribute("aria-hidden", "true");
  zeichen.textContent = LAGE_ZEICHEN[z.art];
  const text = document.createElement("span");
  text.className = "einst-lage-text";
  const titel = document.createElement("span");
  titel.className = "einst-lage-titel";
  titel.textContent = z.titel;
  text.appendChild(titel);
  if (z.satz) {
    const satz = document.createElement("span");
    satz.className = "einst-lage-satz";
    satz.textContent = z.satz;
    text.appendChild(satz);
  }
  const wert = document.createElement("span");
  wert.className = "einst-lage-wert";
  wert.dataset.art = z.art;
  wert.textContent = z.wert;
  li.append(zeichen, text, wert);
  return li;
}

/**
 * Die Karte. Nur Konto 1: sonst `null` (kein Element, kein Abruf).
 * Der Takt läuft nur, solange die Karte im Dokument hängt und der Tab sichtbar ist.
 */
export function baueLageKarte(k: LageKontext): HTMLElement | null {
  if (!entwicklerImChatFuerKonto(k.uid)) return null;
  const hole = k.hole ?? standVomServer;
  const sichtbar = k.sichtbar ?? einstellungenOffen;

  const abschnitt = document.createElement("section");
  abschnitt.className = "einst-gerade einst-lage";
  abschnitt.setAttribute("aria-label", "Was gerade läuft");
  const titel = document.createElement("h3");
  titel.className = "einst-gruppe-titel";
  titel.textContent = "Was gerade läuft";
  const liste = document.createElement("ul");
  liste.className = "einst-gerade-karte einst-lage-liste";
  liste.setAttribute("role", "status");
  liste.setAttribute("aria-live", "off"); // leise: der Takt soll nichts vorlesen
  abschnitt.append(titel, liste);

  let letzter: LageStand | null = null;
  const zeige = (stand: LageStand | null): void => {
    letzter = stand;
    liste.replaceChildren(...lageZeilen(stand, Date.now(), k.konto?.()).map(zeileDom));
  };
  // Schaltet Martin einen Entwickler um, zeigt die Karte es sofort — ohne neue Frage an den Server.
  const beiKonto = (): void => {
    if (!abschnitt.isConnected) return;
    zeige(letzter);
  };
  window.addEventListener(ENTWICKLER_KONTO_EREIGNIS, beiKonto);
  zeige(null);
  liste.querySelectorAll<HTMLElement>(".einst-lage-wert").forEach((w) => (w.textContent = "wird abgefragt …"));

  let laeuft = false;
  const hol = (): void => {
    if (laeuft) return;
    laeuft = true;
    void hole()
      .catch(() => null)
      .then((stand) => {
        if (stand === "ende") {
          stoppe(); // abgemeldet oder kein Betreiber: nie wieder fragen
          zeige(null);
          return;
        }
        zeige(stand);
      })
      .finally(() => {
        laeuft = false;
      });
  };

  let uhr: ReturnType<typeof setInterval> | null = null;
  const stoppe = (): void => {
    if (uhr !== null) clearInterval(uhr);
    uhr = null;
    document.removeEventListener("visibilitychange", beiSicht);
    window.removeEventListener(ENTWICKLER_KONTO_EREIGNIS, beiKonto);
  };
  const tick = (): void => {
    if (!abschnitt.isConnected) {
      stoppe(); // die Karte ist weg (Seitenwechsel, Neuaufbau) — der Takt endet mit ihr
      return;
    }
    if (document.visibilityState === "visible" && sichtbar()) hol();
  };
  function beiSicht(): void {
    if (!abschnitt.isConnected) return stoppe();
    if (document.visibilityState === "visible" && sichtbar()) hol();
  }
  uhr = setInterval(tick, k.takt ?? LAGE_ABRUF_MS);
  document.addEventListener("visibilitychange", beiSicht);
  hol();
  return abschnitt;
}
